import { Entity, Task, TaskResponse, TaskState } from 'gql/graphql';
import { useContext, useEffect, useState } from 'react';
import { EntityContext, TaskContext } from '../EntityDocs';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { ArrowRightOutlined } from '@ant-design/icons';
import ButtonEditDoc from './ButtonEditDoc';
import ButtonConfirmDoc from './ButtonConfirmDoc';
import DropdownEntities from '../../EntityGuarantorCreation/DropdownEntities/DropdownEntities';
import CardUploadResponse from '../../components/Atoms/Cards/CardUploadResponse/CardUploadResponse';
import SectionIssues from '../../../SectionIssues/SectionIssues';
import ActionAcceptAndReject from '../../../ActionAcceptAndReject/ActionAcceptAndReject';
import AddAnotherDoc from '../../components/Organisms/AddAnotherDoc/AddAnotherDoc';
import CardBorrowerImpersonation from '../../../CardBorrowerImpersonation/CardBorrowerImpersonation';

interface Props {
  closeTask: any;
  isConfirmDocs: boolean;
  onSubmitChangeEntity: any;
}

const Step1 = ({ closeTask, isConfirmDocs, onSubmitChangeEntity }: Props) => {
  const task: Task | any = useContext(TaskContext);
  const entity: Entity | any = useContext(EntityContext);

  const { data: taskResponses } = useGetTaskResponsesByTask({ 
    variables: { findResponsesByTask: { taskId: task._id } } 
  });

  const [responseEin, setResponseEin] = useState<TaskResponse>();
  const [responseCertificate, setResponseCertificate] = useState<TaskResponse>();
  const [responseOperating, setResponseOperating] = useState<TaskResponse>();
  const [responseGoodStanding, setResponseGoodStanding] = useState<TaskResponse>();
  const [isModalEdit, setModalEdit] = useState<boolean>(false);

  useEffect(() => {
    let einLetter: any = taskResponses?.find((item) => item.key === 'ein-letter');
    setResponseEin(einLetter);

    let certificate: any = taskResponses?.find((item) => item.key === 'certificate-organization');
    if (certificate) setResponseCertificate(certificate);

    let operating: any = taskResponses?.find((item) => item.key === 'operating-agreement');
    setResponseOperating(operating);

    let goodStanding: any = taskResponses?.find((item) => item.key === 'certificate-good-standing');
    if (goodStanding) setResponseGoodStanding(goodStanding);

  }, [taskResponses]);


  let docs = [
    {
      title: 'EIN letter',
      key: 'ein-letter',
      state: responseEin,
      description:
        'An official document from the IRS confirming your business’s federal tax ID number.',
      nameButtonDescription: 'Get yours directly from the IRS here',
      linkButtonDescription: 'https://www.irs.gov/businesses/small-businesses-self-employed/get-an-employer-identification-number'
    },
    {
      title: 'Certificate of organization',
      key: 'certificate-organization',
      state: responseCertificate,
      description:
        'Filed with your state (usually online) to officially create your LLC. Also known as Articles of Organization or Certificate of Formation.',
      nameButtonDescription: 'See a sample',
      linkButtonDescription: 'https://www.investopedia.com/terms/a/articles-of-organization.asp'
    },
    {
      title: 'Operating Agreement',
      key: 'operating-agreement',
      state: responseOperating,
      description:
        'An internal document showing who owns your LLC and how it’s run. Must be signed by all members. Also called an LLC Agreement or Company Agreement.',
      nameButtonDescription: 'See a sample',
      linkButtonDescription: 'https://eforms.com/operating-agreements/'
    },
    {
      title: 'Certificate of Good Standing',
      key: 'certificate-good-standing',
      state: responseGoodStanding,
      description:
        'Requested from your state’s website to prove your LLC is active and compliant. Also called a Certificate of Status or Letter of Good Standing.',
      nameButtonDescription: 'See a sample',
      linkButtonDescription: 'https://business.ct.gov/knowledge-base/articles/sots/certificates-of-legal-existence?language=en_US'
    }
  ];

  if (isConfirmDocs)
    docs = docs.filter((item) => item.key !== 'ein-letter' && item.key !== 'operating-agreement')

  return (
    <section className="entity-docs-step-2">
      <div className="section-1">
        <h3 className="h3">
          {isConfirmDocs && (
            <>
              Confirm or edit the documents for{' '}
              <DropdownEntities onSubmit={onSubmitChangeEntity} isReviewBold />
            </>
          )}
          {!isConfirmDocs && (
            <>
              Here are the docs we need for{' '}
              <DropdownEntities onSubmit={onSubmitChangeEntity} isReviewBold />
            </>
          )}
        </h3>

        <CardBorrowerImpersonation hasResponses={isConfirmDocs} />

        {isConfirmDocs && (
          <div className="h4 h4-w4">
            Editing won’t affect closed loan applications, but if you have any
            active applications, the entity details will be updated there. Don’t
            want anything to change?{' '}
            <b className="bold cursor">Create a new entity instead</b>{' '}
            <ArrowRightOutlined className="cursor" />
          </div>
        )}
        {isConfirmDocs && (
          <div className="mt-40">
            <span style={{ fontWeight: 800 }}>New documents needed</span>
            <p style={{ marginTop: 6 }}>
              Your property location or entity formation date can affect the
              documentation we need to process your loan
            </p>
          </div>
        )}
      </div>

      <SectionIssues task={task} />

      {docs.map((itemDoc: any) =>
        <div key={itemDoc.key} className="mt-20">
          <CardUploadResponse
            title={itemDoc.title}
            description={itemDoc.description}
            isButtonSample={{ show: false, link: '' }}
            isDescriptionButton={{ show: true, name: itemDoc.nameButtonDescription, link: itemDoc.linkButtonDescription}}
            submit={{
              title: 'Submit for review',
              loading: false,
              disabled: true,
              onClick: () => { }
            }}
            files={itemDoc.state?.files}
            state={itemDoc.state?.state}
            insertHtml={
              itemDoc.state &&
              <ActionAcceptAndReject
                taskResponseId={itemDoc.state._id}
                task={{ id: task._id, key: task.key, name: 'Entity Docs' }}
                files={itemDoc.state?.files}
                currentState={itemDoc.state?.state}
              />
            }
          />
        </div>
      )}
      {isConfirmDocs && (
        <div className="mt-40">
          <span style={{ fontWeight: 800 }}>Submitted documents</span>
          <div className="mt-20">
            <CardUploadResponse
              title="EIN letter"
              files={responseEin?.files}
              state={
                responseEin?.state !== TaskState.Incomplete &&
                  responseEin?.state !== TaskState.Draft
                  ? responseEin?.state
                  : undefined
              }
              insertHtml={
                responseEin &&
                <ActionAcceptAndReject
                  taskResponseId={responseEin?._id}
                  task={{ id: task._id, key: task.key, name: 'Entity Docs' }}
                  files={responseEin?.files}
                  currentState={responseEin?.state}
                />
              }
            />
          </div>
          <div className="mt-20">
            <CardUploadResponse
              title="Operating Agreement"
              files={responseOperating?.files}
              state={
                responseOperating?.state !== TaskState.Incomplete &&
                  responseOperating?.state !== TaskState.Draft
                  ? responseOperating?.state
                  : undefined
              }
              insertHtml={
                responseOperating &&
                <ActionAcceptAndReject
                  taskResponseId={responseOperating?._id}
                  task={{ id: task._id, key: task.key, name: 'Entity Docs' }}
                  files={responseOperating?.files}
                  currentState={responseOperating?.state}
                />
              }
            />
          </div>

          <div className="mt-20 submits">
            <ButtonConfirmDoc
              isConfirmDocs={isConfirmDocs}
              isModalEdit={isModalEdit}
              setModalEdit={setModalEdit}
              ein={responseEin}
              agreement={responseOperating}
              certificate={responseCertificate}
              goodStanding={responseGoodStanding}
              closeTask={closeTask}
              listFileUpload={[]}
              buttonLabel={
                responseEin?.state === TaskState.Incomplete
                  ? 'Confirm documents'
                  : 'Submit changes'
              }
              buttonShow={
                responseEin?.state === TaskState.Incomplete ||
                responseEin?.state === TaskState.Draft ||
                responseOperating?.state === TaskState.Incomplete ||
                responseOperating?.state === TaskState.Draft
              }
            />
            {responseEin?.state === TaskState.Incomplete && (
              <ButtonEditDoc
                entity={entity?.name}
                ein={responseEin}
                agreement={responseOperating}
              />
            )}
          </div>
        </div>
      )}

      <AddAnotherDoc task={task} />
    </section>
  );
};
export default Step1;
